import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom';

import StatusView from './components/onboardings/StatusView';
import IncomingApplications from './components/onboardings/IncomingApplications';
import AnnouncementManager from './components/announcements/AnnouncementManager';
import NotificationPage from './components/notifications/Notification';
import Login from './components/login/Login';
import SupportTickets from './components/support_tickets/SupportTicket';
import AdminUsersPage from './components/users/userApp';
import CategoryAdminPanel from './components/categories/Category';
import KDPUsersPanel from './components/KDPusers/KDPusers';
import ContactAdminPanel from './components/contact/contactForm';
import OrderManagementSystem from './components/order-invoices/orderandinvoices';
import ServiceBookingManagement from './components/service-bookings/ServiceBookingManagement';
import TutorialSystem from './components/tutorials/tutorial';
import ShopFeesManager from './components/shopfees/shopfeemanager';
import SubscriptionPlanManager from './components/subscription/subscriptionManager';
import AdminWalletDashboard from './components/wallet/wallet';
import HSNManagementPage from './components/hsn/hsn';
import UserDetails from './components/UserDetails/index';
import PrivacyPolicyEditor from './components/privacy-policy/PrivacyPolicyEditor';
import TermsOfUseEditor from './components/terms-of-use/TermsOfUseEditor';
import RefundReturnEditor from './components/refund-return/RefundReturnEditor';
import AgreementEditor from './components/agreement/AgreementEditor';
import KDPInvoices from './components/kdp-invoices/KDPInvoices';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProductPolicyManagement from './components/productPolicyManagement';
import UserManagement from './components/admin/UserManagement';
import RoleManagement from './components/admin/RoleManagement';
import PermissionManagement from './components/admin/PermissionManagement';
import Profile from './components/profile/Profile';
import TokenRefreshLoader from './components/common/TokenRefreshLoader';
import './App.css';
import AttendanceManagement from './components/attendance/AttendanceManagement';
import COODashboard from './components/coo/COODashboard';
import CTODashboard from './components/cto/CTODashboard';
import SelfAttendance from './components/attendance/SelfAttendance';
import SizeChartAdminPanel from './components/size-chart/sizeChart';
import ShopManagement from './components/shops/ShopManagement';
import ShopDetailsPage from './components/shops/ShopDetailsPage';
import ShopOffers from './components/shops/ShopOffers';
import ShopProducts from './components/shops/ShopProducts';
import ServiceCategoryAdmin from './components/service/serviceManagement';
import CustomerSuggestions from './components/customer-suggestions/CustomerSuggestions';
import executiveNavigation from './config/executiveNavigation';
import hrNavigation from './config/hrNavigation';
import operationsNavigation from './config/operationsNavigation';
import techNavigation from './config/techNavigation';
import VisitorAnalytics from './components/visitor-analytics/VisitorAnalytics';
import FieldBoyOperations from './components/field-boy-ops/FieldBoyOperations';
import BloggerOperations from './components/blogger-ops/BloggerOperations';

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}

function AppContent() {
  const { isAuthenticated, loading, isRefreshingToken } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen text-xl text-gray-600">
        Loading...
      </div>
    );
  }

  console.log('User is authenticated:', isAuthenticated);
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Token Refresh Loading Overlay */}
      <TokenRefreshLoader isVisible={isRefreshingToken} />

      <Routes>
        <Route
          path="/login"
          element={!isAuthenticated ? <Login /> : <Navigate to="/" />}
        />
        <Route
          path="/*"
          element={isAuthenticated ? <AuthenticatedApp /> : <Navigate to="/login" />}
        />
      </Routes>
    </div>
  );
}

function AuthenticatedApp() {
  const { user, logout, canAccessModule } = useAuth();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [openRailMenu, setOpenRailMenu] = useState(null);
  const [cachedProfile, setCachedProfile] = useState(() => {
    try {
      const raw = localStorage.getItem('kd_admin_employee_profile');
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });
  
  // UPDATED: Added 'shop' to dropdown states
  const [dropdownStates, setDropdownStates] = useState({
    onboarding: false,
    users: false,
    shop: false, // New shop section
    management: false,
    system: false,
    employee: false,
    executive: false, // Executive Department dropdown
    finance: false, // Finance Department dropdown
    operations: false, // Operations Department dropdown
    tech: false, // Tech Department dropdown
    hr: false // HR Department dropdown
  });
  const navRef = useRef(null);
  const hamburgerRef = useRef(null);
  const userMenuRef = useRef(null);
  const userMenuRefMobile = useRef(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'kd_admin_employee_profile') {
        try {
          setCachedProfile(e.newValue ? JSON.parse(e.newValue) : null);
        } catch (_) {
          setCachedProfile(null);
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Prefer cached employee profile (same as Profile page) so avatar/name match; sync from localStorage when user is set (e.g. after login)
  useEffect(() => {
    if (!user) return;
    try {
      const raw = localStorage.getItem('kd_admin_employee_profile');
      const parsed = raw ? JSON.parse(raw) : null;
      setCachedProfile(parsed ?? user);
    } catch (_) {
      setCachedProfile(user);
    }
  }, [user]);

  // When Profile page updates the cached employee profile (same tab), update dropdown avatar
  useEffect(() => {
    const handler = (e) => {
      if (e.detail) setCachedProfile(e.detail);
    };
    window.addEventListener('kd_admin_employee_profile_updated', handler);
    return () => window.removeEventListener('kd_admin_employee_profile_updated', handler);
  }, []);

  // Close user menu when clicking outside (desktop or mobile)
  useEffect(() => {
    const handleClickOutside = (e) => {
      const inDesktop = userMenuRef.current && userMenuRef.current.contains(e.target);
      const inMobile = userMenuRefMobile.current && userMenuRefMobile.current.contains(e.target);
      if (!inDesktop && !inMobile) setUserMenuOpen(false);
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [userMenuOpen]);

  // Prefer cached employee profile (same source as Profile page) so dropdown shows same avatar/image
  const displayProfile = cachedProfile ?? user;
  // Keep default avatar consistent with Profile page (local asset — no external request)
  const defaultAvatar = '/default-avatar.png';
  const fullName = displayProfile && (displayProfile.firstName || displayProfile.lastName)
    ? `${displayProfile.firstName || ''} ${displayProfile.lastName || ''}`.trim()
    : 'Admin User';
  // Keep avatar path logic consistent with Profile page
  const profileAvatar = displayProfile && (displayProfile.profileImageUrl || displayProfile.avatarUrl || displayProfile.profileImage || displayProfile.imageUrl || displayProfile.avatar);
  const avatarUrl = (profileAvatar && !String(profileAvatar).includes('png.pngtree.com')) ? profileAvatar : defaultAvatar;

  // Define navigation items with their corresponding modules
  const navigationItems = {
    onboarding: [
      { path: '/incoming-applications', label: 'Incoming Applications', module: 'Incoming Applications' },
      { path: '/status/accepted', label: 'Accepted Applications', module: 'Accepted Applications' },
      { path: '/status/rejected', label: 'Rejected Applications', module: 'Rejected Applications' },
      { path: '/status/hold', label: 'Holded Applications', module: 'Holded Applications' }
    ],
    users: [
      { path: '/userApp', label: 'KD Users', module: 'KD Users' },
      { 
        path: '/kdpusers', 
        label: 'KDP Users', 
        module: 'KDP Users',
        isParent: true,
        subItems: [
          { path: '/kdpusers?role=ShopOwner', label: 'Shop Owners', module: 'KDP Users' },
          { path: '/kdpusers?role=DeliveryBoy', label: 'Delivery Boys', module: 'KDP Users' },
          { path: '/kdpusers?role=ShopMember', label: 'Shop Members', module: 'KDP Users' },
          { path: '/kdpusers?role=Admin', label: 'Admins', module: 'KDP Users' },
          { path: '/kdpusers?role=Guest', label: 'Guests', module: 'KDP Users' }
        ]
      }
    ],

  
    shop: [
      { path: '/shops', label: 'Shop Management', module: 'Shop Management' },
      { path: '/shop-offers', label: 'Shop Offers', module: 'Shop Offers' },
      { path: '/shop-products', label: 'Shop Products', module: 'Shop Products' },
    ],
    management: [
      { path: '/orderandinvoices', label: 'Order Details', module: 'Order Details' },
      { path: '/service-bookings', label: 'Service Bookings', module: 'Service Bookings', alternativeModules: ['operations/bpo/service-bookings', 'Order Details', 'operations/bpo/orders', 'orders'] },
      { path: '/category', label: 'Categories', module: 'Categories' },
      { path: '/size-chart', label: 'Size Charts', module: 'Size Charts' },
      { path: '/service-category-management', label: 'Service Categories', module: 'Service Categories' },
      { path: '/shopfees', label: 'Shop Fees', module: 'Shop Fees' },
      { path: '/subscription', label: 'Subscription Plans', module: 'Subscription Plans' },
      { path: '/wallet-dashboard', label: 'Wallet', module: 'Wallet' },
      { path: '/hsn', label: 'HSN Codes', module: 'HSN Codes' },
      { path: '/customer-suggestions', label: 'Customer Suggestions', module: 'Customer Suggestions' },
      { path: '/kdp-invoices', label: 'KDP Invoices',  module: 'KDP Invoices' },
    { path: '/visitor-analytics', label: 'Visitor Analytics', module: 'Visitor Analytics' },

    ],
    system: [
      { path: '/announcements', label: 'Announcements', module: 'Announcements' },
      { path: '/notification', label: 'Notifications', module: 'Notifications' },
      { path: '/support', label: 'Support Tickets', module: 'Support Tickets' },
      { path: '/privacy-policy', label: 'Privacy Policy', module: 'Privacy Policy' },
      { path: '/partner-agreement', label: 'Partner Agreement', module: 'Partner Agreement', alternativeModules: ['executive/legal'] },
      { path: '/terms-of-use', label: 'Terms of Use', module: 'Terms of Use' },
      { path: '/refund-return', label: 'Refund & Return', module: 'Refund & Return' },
      { path: '/contact', label: 'Contact Forms', module: 'Contact Forms' },
      { path: '/tutorial', label: 'Tutorials', module: 'Tutorials' }
    ],
    employee: [
      { path: '/employee-management', label: 'Employee Management', module: 'Employee Management' },
      { path: '/role-management', label: 'Role Management', module: 'Role Management' },
      { path: '/permission-management', label: 'Permission Management', module: 'Permission Management' },
      { path: '/attendance', label: 'Attendance', module: 'Attendance' },
      { path: '/my-attendance', label: 'My Attendance', module: 'My Attendance' }
    ],
    dashboards: [
      { path: '/cto-dashboard', label: 'CTO Dashboard', module: 'CTO Dashboard' },
      { path: '/coo-dashboard', label: "COO Dashboard", module: 'COO Dashboard' }
    ],
    executive: executiveNavigation,
    finance: [
      {
        path: '/finance/accounts',
        label: 'Accounts',
        isParent: true,
        subItems: [
          { path: '/orderandinvoices', label: 'Order Invoices', module: 'finance/orderinvoices', alternativeModules: ['Order Details'] },
          { path: '/kdp-invoices', label: 'KDP Invoices', module: 'finance/kdpinvoices', alternativeModules: ['KDP Invoices'] },
          { path: '/wallet-dashboard', label: 'Wallet Transactions', module: 'finance/wallet transactions', alternativeModules: ['Wallet'] }
        ]
      },
     
    ],
    operations: operationsNavigation,
    tech: techNavigation,
    hr: hrNavigation
  };

  // Check if a single nav item is accessible (module or alternativeModules).
  // Parent items with only subItems (no module) are accessible only if at least one subItem is accessible.
  const isNavItemAccessible = (item) => {
    if (item.module && canAccessModule(item.module)) return true;
    if (item.alternativeModules && item.alternativeModules.some((m) => canAccessModule(m))) return true;
    // Parent with subItems but no module: show only if at least one subItem is accessible
    if (item.subItems && item.subItems.length) {
      return item.subItems.some((sub) => isNavItemAccessible(sub));
    }
    if (!item.module && !(item.alternativeModules && item.alternativeModules.length)) return false;
    return false;
  };

  // Filter navigation items based on permissions. Parents with subItems are shown if directly accessible; subItems filtered by permission.
  const getAccessibleItems = (items) => {
    if (!items || !Array.isArray(items)) return [];
    return items.filter(item => {
      const directAccess = isNavItemAccessible(item);
      if (item.subItems && item.subItems.length) {
        // Parent with subItems: show parent if it's accessible (e.g. no module); subItems are filtered when rendering
        return directAccess;
      }
      return directAccess;
    });
  };

  // Section visible if any top-level item is accessible (don't require subItems to be accessible)
  const hasAccessibleItems = (items) => {
    if (!items || !Array.isArray(items)) return false;
    return items.some(item => isNavItemAccessible(item));
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        navRef.current &&
        !navRef.current.contains(event.target) &&
        hamburgerRef.current &&
        !hamburgerRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    const handleResize = () => {
      if (window.innerWidth > 768 && menuOpen) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [menuOpen]);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    setDropdownStates({
      onboarding: false,
      users: false,
      shop: false,
      management: false,
      system: false,
      employee: false,
      executive: false,
      finance: false,
      operations: false,
      tech: false
    });
  };

  // Handle dropdown toggle
  const toggleDropdown = (dropdownName) => (e) => {
    e.stopPropagation();
    setDropdownStates(prev => {
      const isCurrentlyOpen = prev[dropdownName];
      const newState = {
        onboarding: false,
        users: false,
        shop: false,
        management: false,
        system: false,
        employee: false,
        executive: false,
        finance: false,
        operations: false,
        tech: false
      };
      if (!isCurrentlyOpen) {
        newState[dropdownName] = true;
      }
      return newState;
    });
  };

  const closeMenu = () => {
    setMenuOpen(false);
    setDropdownStates({
      onboarding: false,
      users: false,
      shop: false,
      management: false,
      system: false,
      employee: false,
      executive: false,
      finance: false,
      operations: false,
      tech: false
    });
  };

  const handleLogout = () => {
    console.log('Logout clicked');
    closeMenu();
    logout();
    navigate('/login', { replace: true });
  };

  const collapseSidebar = () => {
    setOpenRailMenu(null);
    setDropdownStates({ onboarding: false, users: false, shop: false, management: false, system: false, employee: false, executive: false, finance: false, operations: false, tech: false });
    setIsSidebarCollapsed(true);
  };

  const expandSidebar = () => {
    setOpenRailMenu(null);
    setIsSidebarCollapsed(false);
  };

  return (
    <div className="app-container">
      {/* Mobile/Tablet top navbar */}
      <nav className="navbar hide-desktop">
        <div className="nav-left">
          <div className="logo">
            <img src="/KD-icon.png" alt="KaamDhanda" className="logo-img" />
            <span className="logo-text">
              KaamDhanda
              <span className="logo-tagline">Link To Your Local Shopping!</span>
            </span>
          </div>

          <div
            ref={hamburgerRef}
            className={`hamburger ${menuOpen ? 'active' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle navigation menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </div>

          {menuOpen && <div className="menu-overlay active" onClick={closeMenu}></div>}

          <div
            ref={navRef}
            className={`nav-links ${menuOpen ? 'active' : ''}`}
          >
            <div className="mobile-header">
              <div className="mobile-workspace-card">
                <img src="/KD-icon.png" alt="Workspace" className="workspace-avatar" />
                <div className="profile-meta">
                  <div className="workspace-name">KaamDhanda</div>
                  <div className="workspace-sub">Workspace</div>
                </div>
              </div>
            </div>

            <div className="mobile-section-title">Executive Department</div>

            {/* Executive Department Dropdown */}
            {hasAccessibleItems(navigationItems.executive) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('executive')}
                  aria-expanded={dropdownStates.executive}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">🏢</span> Executive Department <i className={`arrow ${dropdownStates.executive ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.executive ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.executive).map((item) => (
                    item.subItems ? (
                      <div key={item.path} className="dropdown-submenu">
                        <span className="dropdown-item dropdown-parent">{item.label}</span>
                        <div className="dropdown-submenu-items">
                          {getAccessibleItems(item.subItems).map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="dropdown-item"
                              onClick={closeMenu}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="mobile-section-title">HR Department</div>

            {/* HR Department Dropdown */}
            {hasAccessibleItems(navigationItems.hr) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('hr')}
                  aria-expanded={dropdownStates.hr}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">👥</span> HR Department <i className={`arrow ${dropdownStates.hr ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.hr ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.hr).map((item) => (
                    item.subItems ? (
                      <div key={item.path} className="dropdown-submenu">
                        <span className="dropdown-item dropdown-parent">{item.label}</span>
                        <div className="dropdown-submenu-items">
                          {getAccessibleItems(item.subItems).map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="dropdown-item"
                              onClick={closeMenu}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="mobile-section-title">Finance Department</div>

            {/* Finance Department Dropdown */}
            {hasAccessibleItems(navigationItems.finance) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('finance')}
                  aria-expanded={dropdownStates.finance}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">💰</span> Finance Department <i className={`arrow ${dropdownStates.finance ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.finance ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.finance).map((item) => (
                    item.subItems ? (
                      <div key={item.path} className="dropdown-submenu">
                        <span className="dropdown-item dropdown-parent">{item.label}</span>
                        <div className="dropdown-submenu-items">
                          {getAccessibleItems(item.subItems).map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="dropdown-item"
                              onClick={closeMenu}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="mobile-section-title">Operations Department</div>

            {/* Operations Department Dropdown */}
            {hasAccessibleItems(navigationItems.operations) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('operations')}
                  aria-expanded={dropdownStates.operations}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">⚙️</span> Operations Department <i className={`arrow ${dropdownStates.operations ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.operations ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.operations).map((item) => (
                    item.subItems ? (
                      <div key={item.path} className="dropdown-submenu">
                        <span className="dropdown-item dropdown-parent">{item.label}</span>
                        <div className="dropdown-submenu-items">
                          {getAccessibleItems(item.subItems).map((subItem) => (
                            subItem.subItems ? (
                              <div key={subItem.label} className="dropdown-submenu">
                                <span className="dropdown-item dropdown-parent">{subItem.label}</span>
                                <div className="dropdown-submenu-items">
                                  {getAccessibleItems(subItem.subItems).map((nested) => (
                                    nested.subItems ? (
                                      <div key={nested.label} className="dropdown-submenu">
                                        <span className="dropdown-item dropdown-parent">{nested.label}</span>
                                        <div className="dropdown-submenu-items">
                                          {getAccessibleItems(nested.subItems).map((deep) => (
                                            <Link key={deep.path} to={deep.path} className="dropdown-item" onClick={closeMenu}>{deep.label}</Link>
                                          ))}
                                        </div>
                                      </div>
                                    ) : (
                                      <Link key={nested.path} to={nested.path} className="dropdown-item" onClick={closeMenu}>{nested.label}</Link>
                                    )
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <Link
                                key={subItem.path}
                                to={subItem.path}
                                className="dropdown-item"
                                onClick={closeMenu}
                              >
                                {subItem.label}
                              </Link>
                            )
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="mobile-section-title">Tech Department</div>

            {/* Tech Department Dropdown */}
            {hasAccessibleItems(navigationItems.tech) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('tech')}
                  aria-expanded={dropdownStates.tech}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">💻</span> Tech Department <i className={`arrow ${dropdownStates.tech ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.tech ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.tech).map((item) => (
                    item.subItems ? (
                      <div key={item.path} className="dropdown-submenu">
                        <span className="dropdown-item dropdown-parent">{item.label}</span>
                        <div className="dropdown-submenu-items">
                          {getAccessibleItems(item.subItems).map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="dropdown-item"
                              onClick={closeMenu}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            <div className="mobile-section-title">Main Navigation</div>

            {canAccessModule('COO Dashboard') && (
              <Link to="/coo-dashboard" className="nav-link" onClick={closeMenu}>
                <span className="mobile-icon">📊</span> COO Dashboard
              </Link>
            )}

            {canAccessModule('CTO Dashboard') && (
              <Link to="/cto-dashboard" className="nav-link" onClick={closeMenu}>
                <span className="mobile-icon">📈</span> CTO Dashboard
              </Link>
            )}

            {(canAccessModule('orders') || canAccessModule('operations/bpo/orders')) && (
              <Link to="/" className="nav-link" onClick={closeMenu}>
                <span className="mobile-icon">🏷️</span> Orders & Invoices
              </Link>
            )}

            {(canAccessModule('Service Bookings') || canAccessModule('operations/bpo/service-bookings') || canAccessModule('operations/bpo/orders') || canAccessModule('Order Details') || canAccessModule('orders')) && (
              <Link to="/service-bookings" className="nav-link" onClick={closeMenu}>
                <span className="mobile-icon">🧰</span> Service Bookings
              </Link>
            )}

            {/* Onboarding Management */}
            {hasAccessibleItems(navigationItems.onboarding) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('onboarding')}
                  aria-expanded={dropdownStates.onboarding}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">📋</span> Onboarding <i className={`arrow ${dropdownStates.onboarding ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.onboarding ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.onboarding).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-item"
                      onClick={closeMenu}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* User Management */}
            {hasAccessibleItems(navigationItems.users) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('users')}
                  aria-expanded={dropdownStates.users}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">👥</span> User Management <i className={`arrow ${dropdownStates.users ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.users ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.users).map((item) => (
                    item.subItems ? (
                      <div key={item.path} className="dropdown-submenu">
                        <span className="dropdown-item dropdown-parent">{item.label}</span>
                        <div className="dropdown-submenu-items">
                          {getAccessibleItems(item.subItems).map((subItem) => (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className="dropdown-item"
                              onClick={closeMenu}
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link
                        key={item.path}
                        to={item.path}
                        className="dropdown-item"
                        onClick={closeMenu}
                      >
                        {item.label}
                      </Link>
                    )
                  ))}
                </div>
              </div>
            )}

            {/* Shop Management */}
            {hasAccessibleItems(navigationItems.shop) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('shop')}
                  aria-expanded={dropdownStates.shop}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">🏪</span> Shop Management <i className={`arrow ${dropdownStates.shop ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.shop ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.shop).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-item"
                      onClick={closeMenu}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Business Management */}
            {hasAccessibleItems(navigationItems.management) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('management')}
                  aria-expanded={dropdownStates.management}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">🧩</span> Business Management <i className={`arrow ${dropdownStates.management ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.management ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.management).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-item"
                      onClick={closeMenu}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* System & Support */}
            {hasAccessibleItems(navigationItems.system) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('system')}
                  aria-expanded={dropdownStates.system}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">🛠️</span> System & Support <i className={`arrow ${dropdownStates.system ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.system ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.system).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-item"
                      onClick={closeMenu}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Employee Management */}
            {hasAccessibleItems(navigationItems.employee) && (
              <div className="dropdown">
                <button
                  className="dropdown-toggle"
                  onClick={toggleDropdown('employee')}
                  aria-expanded={dropdownStates.employee}
                  aria-haspopup="true"
                >
                  <span className="mobile-icon">👔</span> Employee Management <i className={`arrow ${dropdownStates.employee ? 'up' : 'down'}`}></i>
                </button>
                <div className={`dropdown-menu ${dropdownStates.employee ? 'show' : ''}`}>
                  {getAccessibleItems(navigationItems.employee).map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="dropdown-item"
                      onClick={closeMenu}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mobile-profile-section">
              <div className="mobile-section-title">Profile</div>
              <div className="mobile-profile-card">
                <img src={avatarUrl} alt={fullName} className="profile-avatar" />
                <div className="profile-meta">
                  <div className="profile-name">{fullName}</div>
                  <Link to="/profile" className="profile-email" onClick={closeMenu}>Open profile</Link>
                </div>
              </div>
              <button className="mobile-logout" onClick={handleLogout} title="Logout">⏻ Logout</button>
            </div>
          </div>
        </div>

        {/* Mobile top-right user menu (Gmail-style) */}
        <div className="nav-right user-menu-nav" ref={userMenuRefMobile}>
          <button
            type="button"
            className="content-user-trigger content-user-trigger--mobile"
            onClick={() => setUserMenuOpen((v) => !v)}
            aria-expanded={userMenuOpen}
            aria-haspopup="true"
            title={fullName}
          >
            <img
              src={avatarUrl}
              alt={fullName}
              className="content-user-avatar profile-avatar"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = defaultAvatar;
              }}
            />
            <span className="content-user-name">{fullName}</span>
            <span className={`content-user-chevron ${userMenuOpen ? 'open' : ''}`} aria-hidden>▼</span>
          </button>
          {userMenuOpen && (
            <div className="content-user-menu content-user-menu--mobile">
              <div className="content-user-menu-header">
                <img
                  src={avatarUrl}
                  alt={fullName}
                  className="content-user-menu-avatar"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = defaultAvatar;
                  }}
                />
                <div className="content-user-menu-meta">
                  <span className="content-user-menu-name">{fullName}</span>
                  <span className="content-user-menu-detail">{displayProfile?.department ?? displayProfile?.dept ?? '—'}</span>
                  <span className="content-user-menu-detail">{displayProfile?.position ?? displayProfile?.designation ?? displayProfile?.role?.name ?? '—'}</span>
                  <span className="content-user-menu-detail">{displayProfile?.employeeId ?? displayProfile?._id ?? '—'}</span>
                </div>
              </div>
              <div className="content-user-menu-divider" />
              <Link to="/profile" className="content-user-menu-item" onClick={() => { setUserMenuOpen(false); closeMenu(); }}>Open profile</Link>
              <button type="button" className="content-user-menu-item content-user-menu-logout" onClick={() => { setUserMenuOpen(false); handleLogout(); }}>Logout</button>
            </div>
          )}
        </div>
      </nav >

      {/* Desktop left sidebar navigation */}
      <aside className={`sidebar-container hide-mobile ${isSidebarCollapsed ? 'collapsed' : ''}`}>
        {/* Collapsed rail */}
        {
          isSidebarCollapsed && (
            <div className="sidebar-rail" onMouseLeave={() => setOpenRailMenu(null)}>
              <button className="rail-icon expand-btn" title="Expand" aria-label="Expand" onClick={expandSidebar}>»</button>
              {canAccessModule('COO Dashboard') && (
                <button className="rail-icon" title="COO Dashboard" aria-label="COO Dashboard" onClick={() => navigate('/coo-dashboard')}>📊</button>
              )}
              {canAccessModule('CTO Dashboard') && (
                <button className="rail-icon" title="CTO Dashboard" aria-label="CTO Dashboard" onClick={() => navigate('/cto-dashboard')}>📈</button>
              )}
              <button className="rail-icon" title="Orders & Invoices" aria-label="Orders" onClick={() => navigate('/')}>🏷️</button>
              
              {hasAccessibleItems(navigationItems.onboarding) && (
                <button className="rail-icon" title="Onboarding" aria-label="Onboarding" onClick={() => setOpenRailMenu(openRailMenu === 'onboarding' ? null : 'onboarding')}>📋</button>
              )}
              {hasAccessibleItems(navigationItems.users) && (
                <button className="rail-icon" title="User Management" aria-label="Users" onClick={() => setOpenRailMenu(openRailMenu === 'users' ? null : 'users')}>👥</button>
              )}
              {/* NEW RAIL ICON: Shop */}
              {hasAccessibleItems(navigationItems.shop) && (
                <button className="rail-icon" title="Shop Management" aria-label="Shop" onClick={() => setOpenRailMenu(openRailMenu === 'shop' ? null : 'shop')}>🏪</button>
              )}
              {hasAccessibleItems(navigationItems.management) && (
                <button className="rail-icon" title="Business Management" aria-label="Management" onClick={() => setOpenRailMenu(openRailMenu === 'management' ? null : 'management')}>🧩</button>
              )}
              {hasAccessibleItems(navigationItems.system) && (
                <button className="rail-icon" title="System & Support" aria-label="System" onClick={() => setOpenRailMenu(openRailMenu === 'system' ? null : 'system')}>🛠️</button>
              )}
              {hasAccessibleItems(navigationItems.employee) && (
                <button className="rail-icon" title="Employee Management" aria-label="Employee" onClick={() => setOpenRailMenu(openRailMenu === 'employee' ? null : 'employee')}>👔</button>
              )}
              {hasAccessibleItems(navigationItems.executive) && (
                <button className="rail-icon" title="Executive Department" aria-label="Executive" onClick={() => setOpenRailMenu(openRailMenu === 'executive' ? null : 'executive')}>🏢</button>
              )}
              {hasAccessibleItems(navigationItems.hr) && (
                <button className="rail-icon" title="HR Department" aria-label="HR" onClick={() => setOpenRailMenu(openRailMenu === 'hr' ? null : 'hr')}>👥</button>
              )}
              {hasAccessibleItems(navigationItems.finance) && (
                <button className="rail-icon" title="Finance Department" aria-label="Finance" onClick={() => setOpenRailMenu(openRailMenu === 'finance' ? null : 'finance')}>💰</button>
              )}
              {hasAccessibleItems(navigationItems.operations) && (
                <button className="rail-icon" title="Operations Department" aria-label="Operations" onClick={() => setOpenRailMenu(openRailMenu === 'operations' ? null : 'operations')}>⚙️</button>
              )}
              {hasAccessibleItems(navigationItems.tech) && (
                <button className="rail-icon" title="Tech Department" aria-label="Tech" onClick={() => setOpenRailMenu(openRailMenu === 'tech' ? null : 'tech')}>💻</button>
              )}
              
              {openRailMenu && (
                <div className="rail-popover">
                  {openRailMenu === 'onboarding' && getAccessibleItems(navigationItems.onboarding).map(item => (
                    <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                  ))}
                  {openRailMenu === 'users' && getAccessibleItems(navigationItems.users).map(item => (
                    item.subItems ? (
                      <div key={item.path} className="rail-submenu">
                        <Link key={item.path} to={item.path} className="rail-popover-item rail-parent-item" onClick={() => setOpenRailMenu(null)}>{item.label} »</Link>
                        {getAccessibleItems(item.subItems).map(subItem => (
                          <Link key={subItem.path} to={subItem.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {subItem.label}</Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                    )
                  ))}
                  
                  {openRailMenu === 'shop' && getAccessibleItems(navigationItems.shop).map(item => (
                    <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                  ))}
                  {openRailMenu === 'management' && getAccessibleItems(navigationItems.management).map(item => (
                    <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                  ))}
                  {openRailMenu === 'system' && getAccessibleItems(navigationItems.system).map(item => (
                    <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                  ))}
                  {openRailMenu === 'employee' && getAccessibleItems(navigationItems.employee).map(item => (
                    <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                  ))}
                  {openRailMenu === 'executive' && getAccessibleItems(navigationItems.executive).map(item => (
                    item.subItems ? (
                      <div key={item.path} className="rail-submenu">
                        <Link key={item.path} to={item.path} className="rail-popover-item rail-parent-item" onClick={() => setOpenRailMenu(null)}>{item.label} »</Link>
                        {getAccessibleItems(item.subItems).map(subItem => (
                          <Link key={subItem.path} to={subItem.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {subItem.label}</Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                    )
                  ))}
                  {openRailMenu === 'hr' && getAccessibleItems(navigationItems.hr).map(item => (
                    item.subItems ? (
                      <div key={item.path} className="rail-submenu">
                        <Link key={item.path} to={item.path} className="rail-popover-item rail-parent-item" onClick={() => setOpenRailMenu(null)}>{item.label} »</Link>
                        {getAccessibleItems(item.subItems).map(subItem => (
                          <Link key={subItem.path} to={subItem.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {subItem.label}</Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                    )
                  ))}
                  {openRailMenu === 'finance' && getAccessibleItems(navigationItems.finance).map(item => (
                    item.subItems ? (
                      <div key={item.path} className="rail-submenu">
                        <Link key={item.path} to={item.path} className="rail-popover-item rail-parent-item" onClick={() => setOpenRailMenu(null)}>{item.label} »</Link>
                        {getAccessibleItems(item.subItems).map(subItem => (
                          <Link key={subItem.path} to={subItem.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {subItem.label}</Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                    )
                  ))}
                  {openRailMenu === 'operations' && getAccessibleItems(navigationItems.operations).map(item => (
                    item.subItems ? (
                      <div key={item.path} className="rail-submenu">
                        <span className="rail-popover-item rail-parent-item">{item.label} »</span>
                        {getAccessibleItems(item.subItems).map(subItem => (
                          subItem.subItems ? (
                            <div key={subItem.label} className="rail-submenu">
                              <span className="rail-popover-item rail-parent-item">→ {subItem.label}</span>
                              {getAccessibleItems(subItem.subItems).map(nested => (
                                nested.subItems ? (
                                  <div key={nested.label} className="rail-submenu">
                                    <span className="rail-popover-item rail-parent-item">→ {nested.label}</span>
                                    {getAccessibleItems(nested.subItems).map(deep => (
                                      <Link key={deep.path} to={deep.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {deep.label}</Link>
                                    ))}
                                  </div>
                                ) : (
                                  <Link key={nested.path} to={nested.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {nested.label}</Link>
                                )
                              ))}
                            </div>
                          ) : (
                            <Link key={subItem.path} to={subItem.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {subItem.label}</Link>
                          )
                        ))}
                      </div>
                    ) : (
                      <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                    )
                  ))}
                  {openRailMenu === 'tech' && getAccessibleItems(navigationItems.tech).map(item => (
                    item.subItems ? (
                      <div key={item.path} className="rail-submenu">
                        <Link key={item.path} to={item.path} className="rail-popover-item rail-parent-item" onClick={() => setOpenRailMenu(null)}>{item.label} »</Link>
                        {getAccessibleItems(item.subItems).map(subItem => (
                          <Link key={subItem.path} to={subItem.path} className="rail-popover-item rail-subitem" onClick={() => setOpenRailMenu(null)}>→ {subItem.label}</Link>
                        ))}
                      </div>
                    ) : (
                      <Link key={item.path} to={item.path} className="rail-popover-item" onClick={() => setOpenRailMenu(null)}>{item.label}</Link>
                    )
                  ))}
                </div>
              )}
              <div className="rail-spacer"></div>
              <Link to="/profile" title={fullName} className="rail-profile" aria-label="Profile">
                <img src={avatarUrl} alt={fullName} />
              </Link>
            </div>
          )
        }

        {/* Expanded full panel */}
        {
          !isSidebarCollapsed && (
            <div className="sidebar-panel">
              <div className="panel-header">
                <div className="workspace-card" onClick={collapseSidebar} title="Collapse sidebar">
                  <img src="/KD-icon.png" alt="Workspace" className="workspace-avatar" />
                  <div className="profile-meta">
                    <div className="workspace-name">KaamDhanda</div>
                    <div className="workspace-sub">Workspace</div>
                  </div>
                  <span className="collapse-icon" aria-hidden="true">«</span>
                </div>
              </div>

              <div className="panel-section">
                <div className="panel-title">Executive Department</div>
                
                {/* Executive Department Dropdown */}
                {hasAccessibleItems(navigationItems.executive) && (
                  <div className="panel-dropdown">
                    <button className="panel-toggle" onClick={toggleDropdown('executive')}>
                      <span className="icon">🏢</span> Executive Department <i className={`arrow ${dropdownStates.executive ? 'up' : 'down'}`}></i>
                    </button>
                    <div className={`panel-dropdown-menu ${dropdownStates.executive ? 'show' : ''}`}>
                      {getAccessibleItems(navigationItems.executive).map((item) => (
                        item.subItems ? (
                          <div key={item.path} className="panel-submenu">
                            <Link to={item.path} className="panel-sublink panel-parent-link">{item.label} »</Link>
                            <div className="panel-submenu-items">
                              {getAccessibleItems(item.subItems).map((subItem) => (
                                <Link key={subItem.path} to={subItem.path} className="panel-sublink panel-subitem">{subItem.label}</Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="panel-title">HR Department</div>
                
                {/* HR Department Dropdown */}
                {hasAccessibleItems(navigationItems.hr) && (
                  <div className="panel-dropdown">
                    <button className="panel-toggle" onClick={toggleDropdown('hr')}>
                      <span className="icon">👥</span> HR Department <i className={`arrow ${dropdownStates.hr ? 'up' : 'down'}`}></i>
                    </button>
                    <div className={`panel-dropdown-menu ${dropdownStates.hr ? 'show' : ''}`}>
                      {getAccessibleItems(navigationItems.hr).map((item) => (
                        item.subItems ? (
                          <div key={item.path} className="panel-submenu">
                            <Link to={item.path} className="panel-sublink panel-parent-link">{item.label} »</Link>
                            <div className="panel-submenu-items">
                              {getAccessibleItems(item.subItems).map((subItem) => (
                                <Link key={subItem.path} to={subItem.path} className="panel-sublink panel-subitem">{subItem.label}</Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="panel-title">Finance Department</div>
                
                {/* Finance Department Dropdown */}
                {hasAccessibleItems(navigationItems.finance) && (
                  <div className="panel-dropdown">
                    <button className="panel-toggle" onClick={toggleDropdown('finance')}>
                      <span className="icon">💰</span> Finance Department <i className={`arrow ${dropdownStates.finance ? 'up' : 'down'}`}></i>
                    </button>
                    <div className={`panel-dropdown-menu ${dropdownStates.finance ? 'show' : ''}`}>
                      {getAccessibleItems(navigationItems.finance).map((item) => (
                        item.subItems ? (
                          <div key={item.path} className="panel-submenu">
                            <Link to={item.path} className="panel-sublink panel-parent-link">{item.label} »</Link>
                            <div className="panel-submenu-items">
                              {getAccessibleItems(item.subItems).map((subItem) => (
                                <Link key={subItem.path} to={subItem.path} className="panel-sublink panel-subitem">{subItem.label}</Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="panel-title">Operations Department</div>
                
                {/* Operations Department Dropdown */}
                {hasAccessibleItems(navigationItems.operations) && (
                  <div className="panel-dropdown">
                    <button className="panel-toggle" onClick={toggleDropdown('operations')}>
                      <span className="icon">⚙️</span> Operations Department <i className={`arrow ${dropdownStates.operations ? 'up' : 'down'}`}></i>
                    </button>
                    <div className={`panel-dropdown-menu ${dropdownStates.operations ? 'show' : ''}`}>
                      {getAccessibleItems(navigationItems.operations).map((item) => (
                        item.subItems ? (
                          <div key={item.path} className="panel-submenu">
                            <span className="panel-sublink panel-parent-link">{item.label} »</span>
                            <div className="panel-submenu-items">
                              {getAccessibleItems(item.subItems).map((subItem) => (
                                subItem.subItems ? (
                                  <div key={subItem.label} className="panel-submenu">
                                    <span className="panel-sublink panel-parent-link">{subItem.label}</span>
                                    <div className="panel-submenu-items">
                                      {getAccessibleItems(subItem.subItems).map((nested) => (
                                        nested.subItems ? (
                                          <div key={nested.label} className="panel-submenu">
                                            <span className="panel-sublink panel-parent-link">{nested.label}</span>
                                            <div className="panel-submenu-items">
                                              {getAccessibleItems(nested.subItems).map((deep) => (
                                                <Link key={deep.path} to={deep.path} className="panel-sublink panel-subitem">{deep.label}</Link>
                                              ))}
                                            </div>
                                          </div>
                                        ) : (
                                          <Link key={nested.path} to={nested.path} className="panel-sublink panel-subitem">{nested.label}</Link>
                                        )
                                      ))}
                                    </div>
                                  </div>
                                ) : (
                                  <Link key={subItem.path} to={subItem.path} className="panel-sublink panel-subitem">{subItem.label}</Link>
                                )
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="panel-title">Tech Department</div>
                
                {/* Tech Department Dropdown */}
                {hasAccessibleItems(navigationItems.tech) && (
                  <div className="panel-dropdown">
                    <button className="panel-toggle" onClick={toggleDropdown('tech')}>
                      <span className="icon">💻</span> Tech Department <i className={`arrow ${dropdownStates.tech ? 'up' : 'down'}`}></i>
                    </button>
                    <div className={`panel-dropdown-menu ${dropdownStates.tech ? 'show' : ''}`}>
                      {getAccessibleItems(navigationItems.tech).map((item) => (
                        item.subItems ? (
                          <div key={item.path} className="panel-submenu">
                            <Link to={item.path} className="panel-sublink panel-parent-link">{item.label} »</Link>
                            <div className="panel-submenu-items">
                              {getAccessibleItems(item.subItems).map((subItem) => (
                                <Link key={subItem.path} to={subItem.path} className="panel-sublink panel-subitem">{subItem.label}</Link>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        )
                      ))}
                    </div>
                  </div>
                )}

                <div className="panel-title" style={{marginTop: '10px'}}>Main Navigation</div>
                <div className="panel-links">
                  {canAccessModule('COO Dashboard') && (
                    <Link to="/coo-dashboard" className="panel-link" onClick={closeMenu}><span className="icon">📊</span> COO Dashboard</Link>
                  )}

                  {canAccessModule('CTO Dashboard') && (
                    <Link to="/cto-dashboard" className="panel-link" onClick={closeMenu}><span className="icon">📈</span> CTO Dashboard</Link>
                  )}

                  {(canAccessModule('orders') || canAccessModule('operations/bpo/orders')) && (
                    <Link to="/" className="panel-link" onClick={closeMenu}><span className="icon">🏷️</span> Orders & Invoices</Link>
                  )}

                  {(canAccessModule('Service Bookings') || canAccessModule('operations/bpo/service-bookings') || canAccessModule('operations/bpo/orders') || canAccessModule('Order Details') || canAccessModule('orders')) && (
                    <Link to="/service-bookings" className="panel-link" onClick={closeMenu}><span className="icon">🧰</span> Service Bookings</Link>
                  )}

                  {hasAccessibleItems(navigationItems.onboarding) && (
                    <div className="panel-dropdown">
                      <button className="panel-toggle" onClick={toggleDropdown('onboarding')}>
                        <span className="icon">📋</span> Onboarding <i className={`arrow ${dropdownStates.onboarding ? 'up' : 'down'}`}></i>
                      </button>
                      <div className={`panel-dropdown-menu ${dropdownStates.onboarding ? 'show' : ''}`}>
                        {getAccessibleItems(navigationItems.onboarding).map((item) => (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAccessibleItems(navigationItems.users) && (
                    <div className="panel-dropdown">
                      <button className="panel-toggle" onClick={toggleDropdown('users')}>
                        <span className="icon">👥</span> User Management <i className={`arrow ${dropdownStates.users ? 'up' : 'down'}`}></i>
                      </button>
                      <div className={`panel-dropdown-menu ${dropdownStates.users ? 'show' : ''}`}>
                        {getAccessibleItems(navigationItems.users).map((item) => (
                          item.subItems ? (
                            <div key={item.path} className="panel-submenu">
                              <Link to={item.path} className="panel-sublink panel-parent-link">{item.label} »</Link>
                              <div className="panel-submenu-items">
                                {getAccessibleItems(item.subItems).map((subItem) => (
                                  <Link key={subItem.path} to={subItem.path} className="panel-sublink panel-subitem">{subItem.label}</Link>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                          )
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAccessibleItems(navigationItems.shop) && (
                    <div className="panel-dropdown">
                      <button className="panel-toggle" onClick={toggleDropdown('shop')}>
                        <span className="icon">🏪</span> Shop Management <i className={`arrow ${dropdownStates.shop ? 'up' : 'down'}`}></i>
                      </button>
                      <div className={`panel-dropdown-menu ${dropdownStates.shop ? 'show' : ''}`}>
                        {getAccessibleItems(navigationItems.shop).map((item) => (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAccessibleItems(navigationItems.management) && (
                    <div className="panel-dropdown">
                      <button className="panel-toggle" onClick={toggleDropdown('management')}>
                        <span className="icon">🧩</span> Business Management <i className={`arrow ${dropdownStates.management ? 'up' : 'down'}`}></i>
                      </button>
                      <div className={`panel-dropdown-menu ${dropdownStates.management ? 'show' : ''}`}>
                        {getAccessibleItems(navigationItems.management).map((item) => (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAccessibleItems(navigationItems.system) && (
                    <div className="panel-dropdown">
                      <button className="panel-toggle" onClick={toggleDropdown('system')}>
                        <span className="icon">🛠️</span> System & Support <i className={`arrow ${dropdownStates.system ? 'up' : 'down'}`}></i>
                      </button>
                      <div className={`panel-dropdown-menu ${dropdownStates.system ? 'show' : ''}`}>
                        {getAccessibleItems(navigationItems.system).map((item) => (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {hasAccessibleItems(navigationItems.employee) && (
                    <div className="panel-dropdown">
                      <button className="panel-toggle" onClick={toggleDropdown('employee')}>
                        <span className="icon">👔</span> Employee Management <i className={`arrow ${dropdownStates.employee ? 'up' : 'down'}`}></i>
                      </button>
                      <div className={`panel-dropdown-menu ${dropdownStates.employee ? 'show' : ''}`}>
                        {getAccessibleItems(navigationItems.employee).map((item) => (
                          <Link key={item.path} to={item.path} className="panel-sublink">{item.label}</Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="panel-footer">
                <div className="sidebar-copyright">
                  2025 © Nirvighna Services Private Limited.
                  <p>All rights reserved.</p>
                </div>
              </div>
            </div>
          )
        }
      </aside >

      <main className={`content with-sidebar ${isSidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
        {/* Top bar with user info on the right (Gmail-style) - desktop only */}
        <header className="content-top-bar hide-mobile">
          <div className="content-top-bar-spacer" />
          <div className="content-top-bar-right" ref={userMenuRef}>
            <button
              type="button"
              className="content-user-trigger"
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-expanded={userMenuOpen}
              aria-haspopup="true"
              title={fullName}
            >
              <img
                src={avatarUrl}
                alt={fullName}
                className="content-user-avatar profile-avatar"
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = defaultAvatar;
                }}
              />
              <span className="content-user-name">{fullName}</span>
              <span className={`content-user-chevron ${userMenuOpen ? 'open' : ''}`} aria-hidden>▼</span>
            </button>
            {userMenuOpen && (
              <div className="content-user-menu">
                <div className="content-user-menu-header">
                  <img
                    src={avatarUrl}
                    alt={fullName}
                    className="content-user-menu-avatar"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = defaultAvatar;
                    }}
                  />
                  <div className="content-user-menu-meta">
                    <span className="content-user-menu-name">{fullName}</span>
                    <span className="content-user-menu-detail">{displayProfile?.department ?? displayProfile?.dept ?? '—'}</span>
                    <span className="content-user-menu-detail">{displayProfile?.position ?? displayProfile?.designation ?? displayProfile?.role?.name ?? '—'}</span>
                    <span className="content-user-menu-detail">{displayProfile?.employeeId ?? displayProfile?._id ?? '—'}</span>
                  </div>
                </div>
                <div className="content-user-menu-divider" />
                <Link to="/profile" className="content-user-menu-item" onClick={() => setUserMenuOpen(false)}>Open profile</Link>
                <button type="button" className="content-user-menu-item content-user-menu-logout" onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>
        </header>

        <div className="content-body">
        <Routes>
          {(canAccessModule('Order Details') || canAccessModule('operations/bpo/orders')) && <Route path="/" element={<OrderManagementSystem />} />}
          {(canAccessModule('Service Bookings') || canAccessModule('operations/bpo/service-bookings') || canAccessModule('Order Details') || canAccessModule('operations/bpo/orders') || canAccessModule('orders')) && (
            <Route path="/service-bookings" element={<ServiceBookingManagement />} />
          )}

          {(canAccessModule('Incoming Applications') || canAccessModule('operations/bpo/onboarding/Incoming Applications')) && <Route path="/incoming-applications" element={<IncomingApplications />} />}
          {(canAccessModule('Accepted Applications') || canAccessModule('operations/bpo/onboarding/Accepted Applications') || canAccessModule('operations/bpo/onboarding/Rejected Applications')) && <Route path="/status/:statusType" element={<StatusView />} />}
          {(canAccessModule('KD Users') || canAccessModule('operations/bpo/users/KD Users')) && <Route path="/user/:userId" element={<UserDetails />} />}
          {(canAccessModule('KD Users') || canAccessModule('operations/bpo/users/KD Users')) && <Route path="/userApp" element={<AdminUsersPage />} />}
          {(canAccessModule('Announcements') || canAccessModule('operations/bpo/apps/Announcements')) && <Route path="/announcements" element={<AnnouncementManager />} />}
          {canAccessModule('Notifications') && <Route path="/notification" element={<NotificationPage />} />}
          {(canAccessModule('Support Tickets') || canAccessModule('operations/bpo/support/Support Tickets')) && <Route path="/support" element={<SupportTickets />} />}
          {(canAccessModule('Categories') || canAccessModule('operations/bpo/apps/Categories')) && <Route path="/category" element={<CategoryAdminPanel />} />}
          {(canAccessModule('Product Policies') || canAccessModule('executive/legal')) && (
            <Route path="/productPolicyManagement" element={<ProductPolicyManagement />} />
          )}
          {(canAccessModule('KDP Users') || canAccessModule('operations/bpo/users/KDP Users')) && <Route path="/kdpusers" element={<KDPUsersPanel />} />}
          {(canAccessModule('Privacy Policy') || canAccessModule('executive/legal')) && (
            <Route path="/privacy-policy" element={<PrivacyPolicyEditor />} />
          )}
          {(canAccessModule('Partner Agreement') || canAccessModule('executive/legal')) && (
            <Route path="/partner-agreement" element={<AgreementEditor />} />
          )}
          {(canAccessModule('Terms of Use') || canAccessModule('executive/legal')) && (
            <Route path="/terms-of-use" element={<TermsOfUseEditor />} />
          )}
          {(canAccessModule('Refund & Return') || canAccessModule('executive/legal')) && (
            <Route path="/refund-return" element={<RefundReturnEditor />} />
          )}
          {(canAccessModule('Contact Forms') || canAccessModule('operations/bpo/support/Contact Forms')) && <Route path="/contact" element={<ContactAdminPanel />} />}
          {(canAccessModule('Order Details') || canAccessModule('finance/orderinvoices') || canAccessModule('operations/bpo/orders')) && <Route path="/orderandinvoices" element={<OrderManagementSystem />} />}
          {(canAccessModule('Shop Fees') || canAccessModule('operations/Shop Fees')) && <Route path="/shopfees" element={<ShopFeesManager />} />}
          {(canAccessModule('Subscription Plans') || canAccessModule('operations/Subscription Plans')) && <Route path="/subscription" element={<SubscriptionPlanManager />} />}

          {(canAccessModule('Wallet') || canAccessModule('finance/wallet transactions')) && <Route path="/wallet-dashboard" element={<AdminWalletDashboard />} />}
          {(canAccessModule('Attendance') || canAccessModule('hr/Employee Management/Attendance')) && <Route path="/attendance" element={<AttendanceManagement />} />}

          {(canAccessModule('HSN Codes') || canAccessModule('operations/HSN Codes') || canAccessModule('operations/bpo/apps/HSN Codes')) && <Route path="/hsn" element={<HSNManagementPage />} />}
          {(canAccessModule('Tutorials') || canAccessModule('operations/bpo/apps/Tutorials') || canAccessModule('executive/marketing')) && (
            <Route path="/tutorial" element={<TutorialSystem />} />
          )}
          {(canAccessModule('Employee Management') || canAccessModule('hr/Employee Management/Employee Management')) && <Route path="/employee-management" element={<UserManagement />} />}
          {(canAccessModule('Role Management') || canAccessModule('hr/Employee Management/Role Management')) && <Route path="/role-management" element={<RoleManagement />} />}
          {(canAccessModule('Permission Management') || canAccessModule('hr/Employee Management/Permission Management')) && <Route path="/permission-management" element={<PermissionManagement />} />}
          {canAccessModule('COO Dashboard') && <Route path="/coo-dashboard" element={<COODashboard />} />}
          {canAccessModule('CTO Dashboard') && <Route path="/cto-dashboard" element={<CTODashboard />} />}
          {(canAccessModule('Size Charts') || canAccessModule('operations/bpo/apps/Size Charts')) && <Route path="/size-chart" element={<SizeChartAdminPanel />} />}
          {(canAccessModule('Shop Management') || canAccessModule('operations/Shop Management')) && <Route path="/shops" element={<ShopManagement />} />}
          {(canAccessModule('Shop Management') || canAccessModule('operations/Shop Management')) && <Route path="/shops/:id" element={<ShopDetailsPage />} />}
          {canAccessModule('Shop Offers') && <Route path="/shop-offers" element={<ShopOffers />} />}
          {canAccessModule('Shop Products') && <Route path="/shop-products" element={<ShopProducts />} />}
          {(canAccessModule('Service Categories') || canAccessModule('operations/bpo/apps/Service Categories')) && <Route path="/service-category-management" element={<ServiceCategoryAdmin />} />}
          {(canAccessModule('Customer Suggestions') || canAccessModule('operations/bpo/support/Customer Suggestions')) && <Route path="/customer-suggestions" element={<CustomerSuggestions />} />}
          {(canAccessModule('KDP Invoices') || canAccessModule('finance/kdpinvoices')) && <Route path="/kdp-invoices" element={<KDPInvoices />} />}

          {/* Website Visitor Analytics */}
          {canAccessModule('Visitor Analytics') && <Route path="/visitor-analytics" element={<VisitorAnalytics />} />}

          {(canAccessModule('operations/Field Boy') ||
            canAccessModule('operations/Shop Management') ||
            canAccessModule('Shop Management') ||
            canAccessModule('operations/bpo/support/Support Tickets') ||
            canAccessModule('Support Tickets')) && (
            <Route path="/field-boy" element={<FieldBoyOperations />} />
          )}

          {(canAccessModule('operations/Blogger') ||
            canAccessModule('operations/Shop Management') ||
            canAccessModule('Shop Management') ||
            canAccessModule('operations/bpo/support/Support Tickets') ||
            canAccessModule('Support Tickets')) && (
            <Route path="/blogger" element={<BloggerOperations />} />
          )}

          <Route path="/profile" element={<Profile />} />
          {(canAccessModule('My Attendance') || canAccessModule('hr/Employee Management/My Attendance')) && <Route path="/my-attendance" element={<SelfAttendance />} />}

        </Routes>
        </div>
      </main>
    </div >
  );
}

export default App;